import { motion, useReducedMotion } from "framer-motion";

/**
 * Hero ambient background — soft radial pulses and 2-3 drifting connection lines.
 * Sits at 8-12% opacity max; supports the "live network" idea without stealing
 * attention from the headline/CTAs.
 *
 * Performance: SVG + transform/opacity only. No filters during scroll.
 */
const dots = [
  // x%, y%, color, size, delay
  { x: 12, y: 22, c: "var(--color-primary, #FF6B47)", size: 14, delay: 0.0 },
  { x: 78, y: 18, c: "var(--color-secondary, #FFC300)", size: 18, delay: 1.4 },
  { x: 38, y: 72, c: "var(--color-primary, #FF6B47)", size: 22, delay: 2.6 },
  { x: 85, y: 78, c: "var(--color-secondary, #FFC300)", size: 12, delay: 0.8 },
  { x: 56, y: 32, c: "var(--color-primary, #FF6B47)", size: 16, delay: 3.4 },
  { x: 22, y: 55, c: "var(--color-secondary, #FFC300)", size: 14, delay: 2.0 },
];

const lines = [
  { x1: 12, y1: 22, x2: 56, y2: 32 },
  { x1: 78, y1: 18, x2: 85, y2: 78 },
  { x1: 38, y1: 72, x2: 22, y2: 55 },
];

export default function AmbientLiveMap() {
  const reduce = useReducedMotion();
  if (reduce) return null;

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.10] mix-blend-screen"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      data-testid="hero-ambient-map"
    >
      {/* Drifting connection lines (very faint, slow dash drift) */}
      {lines.map((l, i) => (
        <motion.line
          key={`l${i}`}
          x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke="white"
          strokeWidth="0.15"
          strokeDasharray="2 3"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{
            pathLength: [0, 1, 1, 0],
            opacity: [0, 0.6, 0.6, 0],
          }}
          transition={{
            duration: 6 + i,
            repeat: Infinity,
            delay: i * 0.9,
            ease: "easeInOut",
          }}
        />
      ))}
      {/* Radial pulses — soft, infrequent */}
      {dots.map((d, i) => (
        <g key={`d${i}`}>
          {/* expanding ring */}
          <motion.circle
            cx={d.x} cy={d.y}
            r={0.4}
            fill="none"
            stroke={d.c}
            strokeWidth="0.15"
            initial={{ r: 0.4, opacity: 0 }}
            animate={{ r: [0.4, 3.2], opacity: [0.9, 0] }}
            transition={{
              duration: 3.2,
              repeat: Infinity,
              delay: d.delay,
              ease: "easeOut",
            }}
          />
          {/* core dot */}
          <motion.circle
            cx={d.x} cy={d.y}
            r={d.size / 25}
            fill={d.c}
            initial={{ opacity: 0.6 }}
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{
              duration: 2.6,
              repeat: Infinity,
              delay: d.delay,
              ease: "easeInOut",
            }}
          />
        </g>
      ))}
    </svg>
  );
}
